import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts'
import { useReconciliationStats } from '@/hooks/useDashboard'

interface Props {
  days: number
}

export function ReconciliationStatsCard({ days }: Props) {
  const { data, isLoading, isError } = useReconciliationStats(days)

  const points = data ?? []
  const totals = points.reduce(
    (acc, p) => {
      acc.matched += p.matched
      acc.total += p.total
      return acc
    },
    { matched: 0, total: 0 }
  )
  const overallPct = totals.total > 0 ? (totals.matched / totals.total) * 100 : 0

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Reconciliation match rate</CardTitle>
        <p className="text-xs text-neutral-500">
          Last {days} days
          {totals.total > 0 ? ` · ${totals.matched} of ${totals.total} matched` : ''}
        </p>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="h-32 animate-pulse rounded bg-neutral-100" />}
        {isError && <div className="text-sm text-red-600">Could not load reconciliation stats.</div>}
        {!isLoading && !isError && totals.total === 0 && (
          <div className="text-sm text-neutral-500">No matches created in this window.</div>
        )}
        {!isLoading && !isError && totals.total > 0 && (
          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Overall match rate
              </div>
              <div className="mt-0.5 text-2xl font-semibold text-neutral-950">
                {overallPct.toFixed(1)}%
              </div>
            </div>
            <div className="h-20 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <YAxis hide domain={[0, 100]} />
                  <Tooltip
                    formatter={(v: number) => `${v.toFixed(1)}%`}
                    labelFormatter={(l) => l}
                  />
                  <Line
                    type="monotone"
                    dataKey="match_rate_pct"
                    stroke="#0000ff"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
